import React from 'react';
import Icon from '../AppIcon';
import Image from '../AppImage';
import Button from './Button';

const AnonymousProfilePreview = ({ 
  onClose,
  onEditProfile,
  privacyLevel = 'high',
  photoBlurred = true,
  userProfile = null
}) => {
  // Use actual user profile data; fallback to minimal placeholders only if no profile
  const displayName = userProfile?.displayName
    ? (privacyLevel === 'high' ? 'Anonymous User' : userProfile.displayName)
    : 'Anonymous User';
  const age = userProfile?.age ?? '';
  const location = privacyLevel === 'high' ? 'Hidden' : (userProfile?.city ? `${userProfile.city}` : 'Not set');
  const bio = userProfile?.bio?.trim() || 'No bio added yet.';
  const interests = Array.isArray(userProfile?.interests) && userProfile.interests.length > 0
    ? userProfile.interests
    : [];
  const photos = (userProfile?.photos && userProfile.photos.length > 0)
    ? userProfile.photos
    : (userProfile?.primaryPhoto ? [userProfile.primaryPhoto] : []);
  const primaryPhoto = userProfile?.primaryPhoto || photos?.[0];
  const photoAlt = userProfile?.primaryPhotoAlt || 'Profile';

  const getBlurClass = () => {
    if (!photoBlurred) return '';
    switch (privacyLevel) {
      case 'high':
        return 'blur-lg';
      case 'medium':
        return 'blur-md';
      case 'low':
        return 'blur-sm';
      default:
        return 'blur-lg';
    }
  };

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md bg-card rounded-xl shadow-warm-2xl animate-scale-in overflow-hidden">
        <div className="relative h-64 bg-gradient-to-br from-primary/20 to-secondary/20">
          {primaryPhoto ? (
            <Image
              src={primaryPhoto}
              alt={photoAlt}
              className={`w-full h-full object-cover ${getBlurClass()}`}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted">
              <Icon name="User" size={64} className="text-muted-foreground" />
            </div>
          )}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-card/90 rounded-full hover:bg-card transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-2xl font-heading font-semibold text-white drop-shadow-lg">
                {displayName}
              </h2>
              {age && (
                <span className="px-2 py-1 bg-card/90 rounded-lg text-sm font-medium">
                  {age}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-white/90 drop-shadow">
              <Icon name="MapPin" size={16} />
              <span className="text-sm">{location}</span>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg">
            <Icon name="Eye" size={20} color="var(--color-primary)" />
            <div className="flex-1">
              <p className="text-sm font-medium text-primary">Preview Mode</p>
              <p className="text-xs text-muted-foreground">
                This is how others see your profile with current privacy settings
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-caption font-semibold mb-2">About</h3>
            <p className="text-sm text-muted-foreground">{bio}</p>
          </div>

          <div>
            <h3 className="text-sm font-caption font-semibold mb-2">Interests</h3>
            <div className="flex flex-wrap gap-2">
              {interests.length > 0 ? (
                interests.map((interest, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-muted rounded-full text-xs font-medium"
                  >
                    {interest}
                  </span>
                ))
              ) : (
                <span className="text-sm text-muted-foreground">No interests added yet.</span>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-caption font-semibold mb-2">Privacy Status</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Profile Visibility</span>
                <span className={`font-medium ${
                  privacyLevel === 'high' ? 'text-success' : 
                  privacyLevel === 'medium' ? 'text-warning' : 'text-error'
                }`}>
                  {privacyLevel === 'high' ? 'Anonymous' : 
                   privacyLevel === 'medium' ? 'Limited' : 'Public'}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Photo Blur</span>
                <span className={`font-medium ${photoBlurred ? 'text-success' : 'text-error'}`}>
                  {photoBlurred ? 'Active' : 'Disabled'}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Location</span>
                <span className={`font-medium ${privacyLevel === 'high' ? 'text-success' : 'text-warning'}`}>
                  {privacyLevel === 'high' ? 'Hidden' : 'Approximate'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              fullWidth
              onClick={onClose}
            >
              Close Preview
            </Button>
            <Button
              variant="default"
              fullWidth
              iconName="Edit"
              iconPosition="left"
              onClick={() => {
                onClose();
                if (onEditProfile) onEditProfile();
              }}
            >
              Edit Profile
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnonymousProfilePreview;